/**
 * SellerDashboard — KPI tiles + Top Products list with row-level actions.
 * Extracted from SellerPage.jsx in iter-41.
 */
import React from "react";
import {
  DollarSign, TrendingUp, Users, Package, Store, Pencil, Power, PowerOff,
  PackageX, PackageCheck, Trash2,
} from "lucide-react";
import { useSellerCurrency } from "../../lib/sellerCurrency";


export function Stat({ icon: Icon, label, value, color = "#FAFAFA" }) {
  return (
    <div className="card p-4 relative overflow-hidden group hover:border-[#3D2845] transition">
      <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full opacity-[0.08] group-hover:opacity-[0.14] transition"
        style={{ backgroundColor: color }}/>
      <div className="relative">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}1A` }}>
          <Icon size={16} strokeWidth={2.5} style={{ color }} />
        </div>
        <div className="text-[10px] text-[#A1A1AA] uppercase tracking-widest font-bold mt-3">{label}</div>
        <div className="font-display font-black text-2xl mt-0.5" style={{ color: color === "#FAFAFA" ? "#FAFAFA" : color }}>{value}</div>
      </div>
    </div>
  );
}


export default function SellerDashboard({
  analytics, onEdit, onToggleActive, onToggleAvailability, onDelete,
}) {
  const { fmt } = useSellerCurrency();
  if (!analytics) return null;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat icon={DollarSign} label="Revenue" value={fmt(analytics.total_revenue)} color="#D4FF00" />
        <Stat icon={DollarSign} label="Pending payout" value={fmt(analytics.pending_payout || 0)} color="#00F0FF" />
        <Stat icon={TrendingUp} label="Sales" value={analytics.total_sales} color="#FF007F" />
        <Stat icon={Users} label="Followers" value={analytics.total_followers} color="#00F0FF" />
        <Stat icon={Package} label="Products" value={analytics.product_count} />
        <Stat icon={Store} label="Stores" value={analytics.store_count} />
        <Stat icon={TrendingUp} label="Clicks" value={analytics.total_clicks} />
        <Stat icon={DollarSign} label="Refunded" value={fmt(analytics.refunded_amount || 0)} color="#FF007F" />
      </div>
      {analytics.top_products?.length > 0 && (
        <div className="card p-5">
          <div className="font-display font-bold uppercase text-sm mb-3">Top products</div>
          {analytics.top_products.map(p => {
            const oos = (p.total_stock ?? 0) === 0;
            const inactive = p.active === false;
            return (
              <div key={p.product_id}
                data-testid={`dash-top-${p.product_id}`}
                className="flex items-center gap-3 py-2 border-b border-[#3D2845] last:border-0">
                <img src={p.images?.[0]} className="w-12 h-12 object-cover rounded-lg" alt="" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{p.name}</div>
                  <div className="text-xs text-[#A1A1AA] flex items-center gap-2">
                    <span>{p.sales} sold · {p.clicks} clicks</span>
                    {inactive ? (
                      <span className="text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#A1A1AA]/15 text-[#A1A1AA]">Inactive</span>
                    ) : oos ? (
                      <span className="text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#FF007F]/15 text-[#FF007F]">Not available</span>
                    ) : null}
                  </div>
                </div>
                <div className="font-display font-bold text-[#D4FF00] shrink-0">{fmt(p.price)}</div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => onEdit(p)}
                    data-testid={`dash-edit-${p.product_id}`}
                    title="Edit"
                    className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black flex items-center justify-center">
                    <Pencil size={12}/>
                  </button>
                  <button onClick={() => onToggleActive(p)}
                    data-testid={`dash-toggle-${p.product_id}`}
                    title={inactive ? "Reactivate" : "Deactivate"}
                    className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FFA500] hover:text-black flex items-center justify-center">
                    {inactive ? <Power size={12}/> : <PowerOff size={12}/>}
                  </button>
                  <button onClick={() => onToggleAvailability(p)}
                    data-testid={`dash-oos-${p.product_id}`}
                    title={oos ? "Restock (+10 per size)" : "Mark as not available"}
                    className={`w-7 h-7 rounded-lg bg-[#3D2845] hover:text-black flex items-center justify-center ${
                      oos ? "hover:bg-[#D4FF00] text-[#FF007F]" : "hover:bg-[#FF007F] hover:text-white"
                    }`}>
                    {oos ? <PackageCheck size={12}/> : <PackageX size={12}/>}
                  </button>
                  <button onClick={() => onDelete(p.product_id)}
                    data-testid={`dash-del-${p.product_id}`}
                    title="Delete"
                    className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center">
                    <Trash2 size={12}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
